import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../App";
import {
  statusLabels,
  absenceTypeLabels,
  fineTypeLabels,
  benefitTypeLabels,
  insuranceTypeLabels,
  relationshipLabels
} from "../../lib/constants";

// ==================== EMPLOYEE PORTAL MAIN PAGE ====================
const EmployeePortalPage = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const response = await api.get('/portal/dashboard');
        setDashboardData(response.data);
      } catch (error) {
        console.error('Error fetching portal dashboard:', error);
        toast.error('Erro ao carregar dados do portal');
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const cards = [
    {
      title: 'Férias',
      description: 'Marcar e gerir pedidos de férias',
      icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
      path: '/portal/vacations',
      color: 'from-blue-500 to-cyan-500',
      stat: dashboardData?.vacation?.days_available ? `${dashboardData.vacation.days_available} dias disponíveis` : null,
      badge: dashboardData?.vacation?.pending_requests > 0 ? `${dashboardData.vacation.pending_requests} pendentes` : null
    },
    {
      title: 'Faltas',
      description: 'Comunicar e justificar faltas',
      icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
      path: '/portal/absences',
      color: 'from-amber-500 to-orange-500',
      stat: dashboardData?.absences?.total_this_year ? `${dashboardData.absences.total_this_year} este ano` : null
    },
    {
      title: 'Formações',
      description: 'Inscrição e evidências de formação',
      icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
      path: '/portal/trainings',
      color: 'from-emerald-500 to-teal-500',
      badge: dashboardData?.upcoming_trainings?.length > 0 ? `${dashboardData.upcoming_trainings.length} agendadas` : null
    },
    {
      title: 'Viatura',
      description: 'Gerir viatura, km e coimas',
      icon: 'M13 10V3L4 14h7v7l9-11h-7z',
      path: '/portal/vehicles',
      color: 'from-purple-500 to-pink-500',
      stat: dashboardData?.vehicle ? dashboardData.vehicle.license_plate : 'Sem viatura'
    },
    {
      title: 'Benefícios',
      description: 'Seguros, cheques creche e subsídios',
      icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z',
      path: '/portal/benefits',
      color: 'from-rose-500 to-red-500',
      stat: null
    }
  ];

  return (
    <div data-testid="employee-portal-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans']">Portal do Colaborador</h1>
          <p className="text-muted-foreground">Bem-vindo, {dashboardData?.employee?.name || user?.name}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            dashboardData?.employee?.status === 'active' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30' : 'bg-amber-100 text-amber-700'
          }`}>
            {statusLabels[dashboardData?.employee?.status] || dashboardData?.employee?.status}
          </span>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((card, index) => (
          <div
            key={index}
            onClick={() => navigate(card.path)}
            className="rounded-xl border bg-card p-5 cursor-pointer hover:border-primary/50 transition-all hover:shadow-lg group"
            data-testid={`portal-card-${card.title.toLowerCase()}`}
          >
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={card.icon} />
              </svg>
            </div>
            <h3 className="font-semibold text-lg">{card.title}</h3>
            <p className="text-sm text-muted-foreground">{card.description}</p>
            {card.stat && <p className="text-sm font-medium text-primary mt-2">{card.stat}</p>}
            {card.badge && (
              <span className="inline-block mt-2 px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30">
                {card.badge}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Upcoming Trainings */}
        <div className="rounded-xl border bg-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-lg">Próximas Formações</h2>
            <button onClick={() => navigate('/portal/trainings')} className="text-sm text-primary hover:underline">Ver todas</button>
          </div>
          {dashboardData?.upcoming_trainings?.length > 0 ? (
            <div className="space-y-3">
              {dashboardData.upcoming_trainings.map((t, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                  <div>
                    <p className="font-medium">{t.name}</p>
                    <p className="text-sm text-muted-foreground">{t.date}</p>
                  </div>
                  <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30">
                    {statusLabels[t.status] || t.status}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">Sem formações agendadas</p>
          )}
        </div>

        {/* Vehicle Info */}
        {dashboardData?.vehicle && (
          <div className="rounded-xl border bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-lg">Viatura Atribuída</h2>
              <button onClick={() => navigate('/portal/vehicles')} className="text-sm text-primary hover:underline">Gerir</button>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h8m-8 0a2 2 0 110-4 2 2 0 010 4zm8 0a2 2 0 110-4 2 2 0 010 4zM3 13h18M5 17a2 2 0 01-2-2V9a2 2 0 012-2h14a2 2 0 012 2v6a2 2 0 01-2 2m-12 0h.01M17 17h.01" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-2xl font-bold">{dashboardData.vehicle.license_plate}</p>
                <p className="text-muted-foreground">{dashboardData.vehicle.brand} {dashboardData.vehicle.model}</p>
                <p className="text-sm text-primary">{dashboardData.vehicle.current_km?.toLocaleString('pt-PT')} km</p>
              </div>
            </div>
            {dashboardData.recent_km_logs?.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-muted-foreground mb-2">Últimos registos:</p>
                {dashboardData.recent_km_logs.slice(0, 2).map((log, i) => (
                  <div key={i} className="flex justify-between text-sm py-1">
                    <span>{log.date}</span>
                    <span className="font-medium">{log.km_traveled} km</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeePortalPage;
